import { useEffect, useState } from "react";
import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  FolderOpen, 
  Users, 
  Settings,
  PoundSterling,
  LogOut,
  Menu,
  X,
  Plug,
  TestTube,
  ExternalLink,
  CalendarDays,
  Kanban,
  Image,
  Star,
  CreditCard,
  Bell,
  ClipboardList,
  BarChart3,
  Clock,
  Gift
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OwnerLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("owner_token");
    if (!token) {
      navigate("/owner-login");
      return;
    }

    const fetchOwner = async () => {
      try {
        const response = await axios.get(`${API}/owner/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOwner(response.data);
      } catch (error) {
        console.error("Auth error:", error);
        localStorage.removeItem("owner_token");
        navigate("/owner-login");
      } finally {
        setLoading(false);
      }
    };

    fetchOwner();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("owner_token");
    navigate("/owner-login");
  };

  const navItems = [
    { path: "/owner-dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/owner-dashboard/pipeline", icon: Kanban, label: "Sales Pipeline" },
    { path: "/owner-dashboard/leads", icon: Users, label: "Lead Management" },
    { path: "/owner-dashboard/operations", icon: ClipboardList, label: "Project Operations" },
    { path: "/owner-dashboard/payments", icon: CreditCard, label: "Payments" },
    { path: "/owner-dashboard/bank-settings", icon: CreditCard, label: "Bank Settings" },
    { path: "/owner-dashboard/email-settings", icon: CreditCard, label: "Email Settings" },
    { path: "/owner-dashboard/reminders", icon: Clock, label: "Reminders" },
    { path: "/owner-dashboard/referrals", icon: Gift, label: "Referrals" },
    { path: "/owner-dashboard/analytics", icon: BarChart3, label: "Analytics" },
    { path: "/owner-dashboard/portfolio", icon: Image, label: "Portfolio Manager" },
    { path: "/owner-dashboard/reviews", icon: Star, label: "Review Manager" },
    { path: "/owner-dashboard/pricing", icon: PoundSterling, label: "Pricing Control" },
    { path: "/owner-dashboard/calendar", icon: CalendarDays, label: "Calendar Controls" },
    { path: "/owner-dashboard/notifications", icon: Bell, label: "Notifications" },
    { path: "/owner-dashboard/settings", icon: Settings, label: "Business Settings" },
    { path: "/owner-dashboard/taptrack", icon: ExternalLink, label: "TapTrack Export" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="owner-layout">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <span className="font-semibold text-gray-900">Owner Dashboard</span>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout}>
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside 
          className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
        >
          <div className="h-full flex flex-col">
            {/* Logo */}
            <div className="p-6 border-b border-gray-200">
              <h1 className="text-xl font-bold text-gray-900">DC Tiling</h1>
              <p className="text-sm text-gray-500">Owner Control Panel</p>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path || 
                  (item.path !== "/owner-dashboard" && location.pathname.startsWith(item.path));
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? "bg-primary text-white"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* User Info */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-primary font-semibold">
                    {owner?.name?.charAt(0).toUpperCase() || "O"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{owner?.name}</p>
                  <p className="text-sm text-gray-500 truncate">{owner?.email}</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full justify-center" 
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 lg:ml-0 min-h-screen">
          <Outlet context={{ owner }} />
        </main>
      </div>
    </div>
  );
};

export default OwnerLayout;
